import Big from 'big.js';
import { DateTime } from 'luxon';
import { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import * as models from 'smg/customer-api/models';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import { SetDocumentTitle } from 'smg/dom';
import { HumanFileSize } from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

// DOM element references
let $spinner: HTMLElement;
let $error: HTMLElement;
let $errorMessage: HTMLElement;
let $content: HTMLElement;
let $title: HTMLElement;
let $quotesList: HTMLElement;
let $notFound: HTMLElement;
let $searchParent: HTMLElement;
let $backToSearch: HTMLElement;

void main();

async function main() {
    initElements();

    const qs = QueryString();
    const quoteKey = qs.get('id') || qs.get('key') || qs.get('quote');
    const searchQuery = qs.get('q');

    if (quoteKey) {
        // Direct quote access via query param
        await loadSingleQuote(quoteKey);
    } else if (searchQuery) {
        // Search by email, phone, or quote number
        await searchQuotes(searchQuery);
    } else {
        // No params - show search form
        showSearch();
    }
}

function initElements() {
    $spinner = document.getElementById('quotes-spinner') as HTMLElement;
    $error = document.getElementById('quotes-error') as HTMLElement;
    $errorMessage = document.getElementById('quotes-error-message') as HTMLElement;
    $content = document.getElementById('quotes-content') as HTMLElement;
    $title = document.getElementById('quotes-title') as HTMLElement;
    $quotesList = document.getElementById('quotes-list') as HTMLElement;
    $notFound = document.getElementById('quote-not-found') as HTMLElement;
    $searchParent = document.getElementById('quote-search-parent') as HTMLElement;
    $backToSearch = document.getElementById('back-to-search') as HTMLElement;
}

function showSearch() {
    hideSpinner();
    $searchParent.classList.remove('d-none');
}

async function loadSingleQuote(key: string) {
    try {
        const data = await OpenQuotes({
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteRequest,key,quoteSents',
            q: `key:${key}`,
            max: 1,
        });

        if (data.results.length === 0) {
            showNotFound();
            return;
        }

        const quote = data.results[0];
        const quoteTitle = `Quote #${quote.serial ?? ''}`;
        SetDocumentTitle(quoteTitle);
        $title.textContent = quoteTitle;

        await drawQuotes(data.results);

        hideSpinner();
        $content.classList.remove('d-none');
    } catch (err) {
        if (AxiosStatusCode(err) === 404) {
            showNotFound();
        } else {
            showError(err);
        }
    }
}

async function searchQuotes(q: string) {
    try {
        const data = await OpenQuotes({
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteRequest,key,quoteSents',
            sort: 'created desc',
            q,
        });

        const quotes = data.results;

        if (quotes.length === 0) {
            // Show search form with warning
            hideSpinner();
            $searchParent.classList.remove('d-none');
            const $warning = document.getElementById('empty-search-warning') as HTMLElement;
            $warning.classList.remove('d-none');
            const $searchInput = document.getElementById('quote-search') as HTMLInputElement;
            $searchInput.value = q;
            return;
        }

        $title.textContent = 'Your Quotes';
        $backToSearch.classList.remove('d-none');

        await drawQuotes(quotes);

        hideSpinner();
        $content.classList.remove('d-none');
    } catch (err) {
        showError(err);
    }
}

async function drawQuoteCard(quote: models.Quote): Promise<HTMLElement> {
    const $card = template.Clone('#quote-card-template').firstElementChild as HTMLElement;
    const lineItems = await GetLineItems({ data: 'name,pluralName,product' }).catch(() => new Map<string, models.LineItem>());

    // Quote serial
    const $serial = $card.querySelector('.quote-serial') as HTMLElement;
    $serial.textContent = `Quote #${quote.serial ?? ''}`;

    // Quote date
    const $date = $card.querySelector('.quote-date') as HTMLElement;
    $date.textContent = `Submitted: ${quote.created?.isValid ? quote.created.toLocaleString() : 'N/A'}`;

    // Quote total
    const $total = $card.querySelector('.quote-total') as HTMLElement;
    $total.textContent = DollarFormat(quote.cachedTotal ?? 0);

    // Product summary (plain text)
    const $products = $card.querySelector('.quote-products') as HTMLElement;

    if (quote.quoteLineItems.length) {
        const summaryMap = new Map<string, { qty: number; singular: string; plural: string }>();
        for (const li of quote.quoteLineItems) {
            const lineItem = lineItems.get(li.lineItemID ?? '');
            const singular = li.lineItem || lineItem?.name || li.lineItemModel?.name || 'Product';

            let entry = summaryMap.get(singular);
            if (!entry) {
                const plural = lineItem?.pluralName || `${singular}s`;
                entry = { qty: 0, singular, plural };
                summaryMap.set(singular, entry);
            }

            entry.qty += li.quantity || 0;
        }

        const summaryParts: string[] = [];
        for (const summary of summaryMap.values()) {
            const name = summary.qty !== 1 ? summary.plural : summary.singular;
            summaryParts.push(`${summary.qty.toLocaleString()} ${name}`);
        }
        $products.textContent = summaryParts.join(', ');
    } else {
        $products.textContent = '';
    }

    // Proofs
    const $proofsContainer = $card.querySelector('.quote-proofs') as HTMLElement;

    if (quote.quoteProofs.length) {
        $proofsContainer.classList.remove('d-none');

        const fancyboxGroup = `quote-${quote.id ?? ''}`;

        for (const qp of quote.quoteProofs) {
            if (!qp.proof?.file) {
                continue;
            }

            const urls = GetProofURLs(window.cloudFront, qp.proof.file);
            const $wrapper = template.Clone('#proof-thumb-template').firstElementChild as HTMLElement;
            const $thumb = $wrapper.querySelector('.proof-thumb-link') as HTMLAnchorElement;

            $thumb.href = urls.main;
            $thumb.setAttribute('data-fancybox', fancyboxGroup);
            $thumb.setAttribute('data-caption', qp.proof.name || `Proof ${qp.number ?? ''}`);

            const $img = $thumb.querySelector('img') as HTMLImageElement;
            $img.src = urls.thumb;
            $img.alt = qp.proof.name || `Proof ${qp.number ?? ''}`;

            const $number = $wrapper.querySelector('.proof-thumb-number') as HTMLElement;
            $number.textContent = `Proof #${qp.number ?? ''}`;

            $proofsContainer.appendChild($wrapper);
        }
    }

    // Line items (always shown)
    const $lineItemsContainer = $card.querySelector('.quote-line-items') as HTMLElement;
    drawLineItems(quote, $lineItemsContainer, lineItems);

    // Add to cart button
    const $addToCart = $card.querySelector('.quote-add-to-cart') as HTMLButtonElement;
    $addToCart.addEventListener('click', () => {
        buttons.Start($addToCart);
        void carts.AddCartItem({ quoteID: quote.key }).then(() => {
            $addToCart.classList.remove('btn-success');
            $addToCart.classList.add('btn-outline-success');

            const $icon = $addToCart.querySelector('i');
            $icon?.classList.remove('fa-shopping-cart');
            $icon?.classList.add('fa-check');
        }).finally(() => {
            buttons.Stop($addToCart);
        });
    });

    // Revision link
    const $revisionLink = $card.querySelector('.quote-request-revision') as HTMLAnchorElement;
    if (quote.quoteProofs.length) {
        $revisionLink.href = `${Relref('/revisions')}?id=${quote.key}`;
    } else {
        $revisionLink.parentElement?.removeChild($revisionLink);
    }

    return $card;
}

interface quoteGroup {
    quoteSent: models.QuoteSent;
    quotes: models.Quote[];
}

function latestQuoteSent(quoteSents: models.QuoteSent[]): models.QuoteSent | undefined {
    let latest: models.QuoteSent | undefined;
    let latestCreated: DateTime | undefined;

    for (const quoteSent of quoteSents) {
        const created = quoteSent.created;
        if (!created?.isValid) {
            continue;
        }
        if (!latestCreated || created.toMillis() > latestCreated.toMillis()) {
            latest = quoteSent;
            latestCreated = created;
        }
    }

    return latest;
}

// Group quotes by the send (quoteSentInstanceID) that delivered them, newest
// send first. Quotes that were never sent come back separately so they can
// render after every group with no message card.
function groupQuotesBySend(quotes: models.Quote[]): { groups: quoteGroup[]; unsent: models.Quote[] } {
    const groupsByKey = new Map<string, quoteGroup>();
    const unsent: models.Quote[] = [];

    for (const quote of quotes) {
        const quoteSent = latestQuoteSent(quote.quoteSents);
        if (!quoteSent) {
            unsent.push(quote);
            continue;
        }

        // Very old rows predate send instances — fall back to the row's own id.
        const key = quoteSent.quoteSentInstanceID || quoteSent.id || '';

        let group = groupsByKey.get(key);
        if (!group) {
            group = { quoteSent, quotes: [] };
            groupsByKey.set(key, group);
        }
        group.quotes.push(quote);

        if ((quoteSent.created?.toMillis() ?? 0) > (group.quoteSent.created?.toMillis() ?? 0)) {
            group.quoteSent = quoteSent;
        }
    }

    const groups = [...groupsByKey.values()];
    groups.sort((a, b) => (b.quoteSent.created?.toMillis() ?? 0) - (a.quoteSent.created?.toMillis() ?? 0));

    return { groups, unsent };
}

async function drawQuotes(quotes: models.Quote[]) {
    const { groups, unsent } = groupQuotesBySend(quotes);

    let first = true;

    for (const group of groups) {
        // A dated boundary marker opens every group past the newest so each
        // send reads as its own section — even when it has no message card.
        if (!first) {
            $quotesList.appendChild(drawQuoteGroupBoundary(group.quoteSent.created));
        }

        // Subject-only sends still render a header card — the subject often
        // carries the send's intent (e.g. preliminary vs action needed).
        if (group.quoteSent.content?.trim() || group.quoteSent.subject?.trim() || group.quoteSent.attachments.length) {
            $quotesList.appendChild(drawQuoteSentCard(group.quoteSent, !first));
        }

        for (const quote of group.quotes) {
            const $card = await drawQuoteCard(quote);
            $quotesList.appendChild($card);
        }

        first = false;
    }

    if (unsent.length) {
        if (groups.length) {
            $quotesList.appendChild(drawQuoteGroupBoundary(undefined));
        }

        for (const quote of unsent) {
            const $card = await drawQuoteCard(quote);
            $quotesList.appendChild($card);
        }
    }
}

// The boundary between two sends stamps the send date on a timeline-ish node,
// so back-to-back sends can no longer blur into one continuous list.
function drawQuoteGroupBoundary(created: DateTime | undefined): HTMLElement {
    const $boundary = template.Clone('#quote-group-boundary-template').firstElementChild as HTMLElement;

    const $label = $boundary.querySelector('.quote-group-boundary-label') as HTMLElement;
    $label.textContent = created?.isValid ? `Sent ${created.toLocaleString(DateTime.DATE_MED)}` : 'Not yet sent';

    return $boundary;
}

function drawQuoteSentCard(quoteSent: models.QuoteSent, older: boolean): HTMLElement {
    const $card = template.Clone('#quote-sent-card-template').firstElementChild as HTMLElement;

    if (older) {
        // Older messages stay quiet so the latest one keeps the spotlight.
        $card.classList.add('border-0', 'bg-light');
        const $cardBody = $card.querySelector('.card-body') as HTMLElement;
        $cardBody.classList.remove('p-md-5');
    }

    const content = quoteSent.content?.trim() ?? '';

    const firstName = quoteSent.user?.firstName?.trim();
    const $from = $card.querySelector('.quote-sent-from') as HTMLElement;
    $from.textContent = firstName ? `A message from ${firstName}` : 'A message from our team';

    const $subject = $card.querySelector('.quote-sent-subject') as HTMLElement;
    $subject.textContent = quoteSent.subject?.trim() || 'Your Quote Is Ready';

    const $date = $card.querySelector('.quote-sent-date') as HTMLElement;
    if (quoteSent.created?.isValid) {
        $date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
    }

    const $body = $card.querySelector('.quote-sent-body') as HTMLElement;
    if (content) {
        if (quoteSent.contentType === 'text/html') {
            $body.innerHTML = Linkify(SanitizeRichHTML(content));
        } else {
            $body.innerHTML = Linkify(html.Nl2br(html.Escape(content)));
        }
    } else {
        // Attachment-only sends still get a card — just no message body.
        $body.remove();
    }

    drawQuoteSentAttachments(quoteSent, $card);

    return $card;
}

// Files the rep attached to the send's email: images join a fancybox gallery
// for this send, everything else becomes a compact file chip.
function drawQuoteSentAttachments(quoteSent: models.QuoteSent, $card: HTMLElement) {
    const attachments = quoteSent.attachments.filter(attachment => Boolean(attachment.publicURL.trim()));
    if (!attachments.length) {
        return;
    }

    const $section = $card.querySelector('.quote-sent-attachments') as HTMLElement;
    $section.classList.remove('d-none');
    const $list = $section.querySelector('.quote-sent-attachments-list') as HTMLElement;

    const fancyboxGroup = `quote-sent-attachments-${quoteSent.quoteSentInstanceID || quoteSent.id || ''}`;

    for (const attachment of attachments) {
        const url = attachment.publicURL;
        const name = attachment.name.trim() || 'Attachment';

        if (IsImageURL(name)) {
            const $thumb = template.Clone('#quote-sent-attachment-image-template').firstElementChild as HTMLAnchorElement;
            // Picture's fancyboxThumb upgrade needs the anchor wired and the
            // img inside it before the call.
            $thumb.href = url;
            $thumb.setAttribute('data-fancybox', fancyboxGroup);
            $thumb.setAttribute('data-caption', name);

            const $img = $thumb.querySelector('img') as HTMLImageElement;
            $img.alt = name;
            $img.title = name;

            $list.appendChild($thumb);

            void Picture($img, url, {
                displayWidth: 80,
                fancyboxThumb: true,
                alt: name,
            });
        } else {
            const $chip = template.Clone('#quote-sent-attachment-file-template').firstElementChild as HTMLAnchorElement;
            $chip.href = url;

            const $name = $chip.querySelector('.quote-sent-attachment-file-name') as HTMLElement;
            $name.textContent = name;
            $name.title = name;

            const $size = $chip.querySelector('.quote-sent-attachment-file-size') as HTMLElement;
            $size.textContent = attachment.size ? HumanFileSize(attachment.size) : '';

            $list.appendChild($chip);
        }
    }
}

function drawLineItems(quote: models.Quote, container: HTMLElement, lineItems: Map<string, models.LineItem>) {
    if (!quote.quoteLineItems.length) {
        const $p = document.createElement('p');
        $p.className = 'text-muted';
        $p.textContent = 'No line items';
        container.replaceChildren($p);
        return;
    }

    const fancyboxGroup = `quote-details-${quote.id ?? ''}`;
    const fragment = document.createDocumentFragment();

    for (const li of quote.quoteLineItems) {
        const $li = template.Clone('#quote-line-item-template').firstElementChild as HTMLElement;

        const lineItem = lineItems.get(li.lineItemID ?? '');
        const $name = $li.querySelector('.line-item-name') as HTMLElement;
        $name.textContent = li.lineItem || lineItem?.name || li.lineItemModel?.name || 'Product';

        // Type and size
        const $type = $li.querySelector('.line-item-type') as HTMLElement;
        const $size = $li.querySelector('.line-item-size') as HTMLElement;
        $type.textContent = `Type: ${li.type || li.lineItemType?.name || 'Standard'}`;
        $size.textContent = `Size: ${li.size || li.lineItemTypeSize?.name || 'N/A'}`;

        // Quantity
        const $qty = $li.querySelector('.line-item-quantity') as HTMLElement;
        $qty.textContent = `Qty: ${(li.quantity ?? 0).toLocaleString()}`;

        // Subtotal
        const $subtotal = $li.querySelector('.line-item-subtotal') as HTMLElement;
        const subtotal = Big(li.price || 0).mul(li.quantity || 0);
        $subtotal.textContent = DollarFormat(subtotal);

        // Proof thumbnail
        if (li.quoteProof?.proof?.file) {
            const urls = GetProofURLs(window.cloudFront, li.quoteProof.proof.file);
            const $thumbContainer = $li.querySelector('.line-item-proof-thumb') as HTMLElement;
            const $link = $thumbContainer.querySelector('a') as HTMLAnchorElement;
            const $img = $thumbContainer.querySelector('img') as HTMLImageElement;

            $thumbContainer.classList.remove('d-none');
            $link.href = urls.main;
            $link.setAttribute('data-fancybox', fancyboxGroup);
            $link.setAttribute('data-caption', li.quoteProof.proof.name || `Proof ${li.quoteProof.number ?? ''}`);
            $img.src = urls.thumb;
            $img.alt = li.quoteProof.proof.name || `Proof ${li.quoteProof.number ?? ''}`;

            const $proofNumber = $li.querySelector('.line-item-proof-number') as HTMLElement;
            $proofNumber.classList.remove('d-none');
            const $proofNumberText = $li.querySelector('.line-item-proof-number-text') as HTMLElement;
            $proofNumberText.textContent = `Proof #${li.quoteProof.number ?? ''}`;
        }

        fragment.appendChild($li);
    }

    container.replaceChildren(fragment);
}

function hideSpinner() {
    $spinner.classList.add('d-none');
}

function showError(err: unknown) {
    hideSpinner();
    $errorMessage.textContent = err instanceof Error ? err.message : 'Something went wrong while loading your quotes.';
    $error.classList.remove('d-none');
}

function showNotFound() {
    hideSpinner();
    $notFound.classList.remove('d-none');
}
